import React from "react";
import PageHeader from "../../../components/common/PageHeader";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { useTakeawayHistory } from "../hooks";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  accept: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  reject: "bg-red-100 text-red-700",
};

const statusClass = (status) =>
  STATUS_STYLES[status] || "bg-gray-100 text-gray-700";

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * One order's full detail - product list with add-ons and notes, which
 * is exactly what didn't fit in the old popup and is why this is now a
 * full page instead of a modal.
 */
const OrderCard = ({ order, currencySymbol }) => {
  const products = order.products || [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Order header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div>
          <div className="font-semibold text-gray-900">{order.orderId}</div>
          <div className="text-xs text-gray-500">{formatTime(order.createdAt)}</div>
        </div>
        <div className="text-right">
          <div className="font-bold text-gray-900">
            {currencySymbol}
            {Number(order.amount || 0).toFixed(2)}
          </div>
          <span
            className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusClass(order.status)}`}
          >
            {order.status}
          </span>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Customer */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div>
            <span className="text-gray-500">Customer: </span>
            <span className="text-gray-900 font-medium">{order.name || "Guest"}</span>
          </div>
          <div>
            <span className="text-gray-500">Phone: </span>
            <span className="text-gray-900">{order.phoneNumber || "-"}</span>
          </div>
          {order.address && (
            <div>
              <span className="text-gray-500">Address: </span>
              <span className="text-gray-900">{order.address}</span>
            </div>
          )}
        </div>

        {/* Products */}
        <div className="border border-gray-100 rounded-lg divide-y divide-gray-100">
          {products.map((p, i) => (
            <div key={p._id || i} className="px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-gray-900">
                  {p.productTitle}
                  <span className="text-gray-500 font-normal"> × {p.quantity}</span>
                </div>
                <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                  {currencySymbol}
                  {Number(p.subTotal ?? p.price * p.quantity ?? 0).toFixed(2)}
                </div>
              </div>

              {p.addOns?.length > 0 && (
                <div className="mt-1 text-xs text-indigo-600">
                  +{" "}
                  {p.addOns
                    .map((a) => (a.quantity > 1 ? `${a.name} x${a.quantity}` : a.name))
                    .join(", ")}
                </div>
              )}

              {p.note && (
                <div className="mt-1 text-xs text-gray-500 italic">Note: {p.note}</div>
              )}
            </div>
          ))}
          {products.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No items</div>
          )}
        </div>

        {/* Totals */}
        <div className="flex flex-wrap gap-x-6 gap-y-0.5 text-xs text-gray-500 justify-end">
          <span>
            Subtotal: {currencySymbol}
            {Number(order.subTotal || 0).toFixed(2)}
          </span>
          {order.packagingTotal > 0 && (
            <span>
              Packaging: {currencySymbol}
              {Number(order.packagingTotal).toFixed(2)}
            </span>
          )}
          <span>
            {`GST (${((order.taxrate || 0) * 100).toFixed(0)}%)`}: {currencySymbol}
            {Number(order.taxAmount || 0).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Takeaway Order History Page
 * Lists past takeaway orders (default: today) with date + search
 * (customer name or phone) filters. Full page rather than a popup so
 * there's room to show each product's add-ons and notes in full.
 */
const TakeawayHistoryPage = () => {
  const { date, setDate, search, setSearch, orders, loading, resetToToday } =
    useTakeawayHistory();
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <PageHeader
        showBackButton
        backTo="/takeaway"
        title="Takeaway Order History"
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            {loading ? "Loading..." : `${orders.length} order${orders.length !== 1 ? "s" : ""}`}
          </span>
        }
        search={
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={resetToToday}
              className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
            >
              Today
            </button>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Search by name or phone
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        }
      />

      {loading ? (
        <div className="py-12 text-center text-gray-500 text-sm">Loading...</div>
      ) : orders.length === 0 ? (
        <div className="py-12 text-center text-gray-500 text-sm">
          No takeaway orders found for this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <OrderCard key={order._id} order={order} currencySymbol={currencySymbol} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TakeawayHistoryPage;
